/**
 * Adaptive HLS playback for hero background videos — Rosio House.
 *
 * Shopify encodes every uploaded video into an HLS (.m3u8) adaptive stream that
 * carries 480p / 720p / 1080p renditions. A player reading that manifest picks
 * the right rendition for the viewer's screen and live bandwidth (480p on weak
 * 4G, up to 1080p on WiFi) and switches mid-playback as the connection changes.
 *
 * Safari / iOS play HLS natively in a <video>. Chrome, Android and Firefox do
 * not, so for them we lazy-load hls.js (only when needed, and only once) to add
 * the same adaptive behaviour. A plain MP4 stays as the last-resort fallback.
 *
 * Each target <video> carries: data-hls (m3u8 url), data-mp4 (fallback url),
 * data-hls-lib (theme-asset url of hls.light.min.js).
 */
(function () {
  var libPromise = null;

  function loadLib(url) {
    if (window.Hls) return Promise.resolve();
    if (libPromise) return libPromise;
    libPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return libPromise;
  }

  function playSafe(video) {
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function useMp4(video) {
    if (video.dataset.mp4) {
      video.src = video.dataset.mp4;
      playSafe(video);
    }
  }

  function setup(video) {
    // Guard against double-processing the same element — this script tag can
    // legitimately appear more than once on a page (e.g. a product with
    // multiple gallery videos), and re-running setup() on an already-wired
    // video would attach a second hls.js instance / re-fetch its source.
    if (video.dataset.hlsAttached) return;
    video.dataset.hlsAttached = 'true';

    var hlsUrl = video.dataset.hls;
    var libUrl = video.dataset.hlsLib;

    // Safari / iOS — native HLS, no library needed.
    if (hlsUrl && video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      playSafe(video);
      return;
    }

    // Everything else — bring in hls.js, then attach the adaptive stream.
    if (hlsUrl && libUrl) {
      loadLib(libUrl).then(function () {
        if (window.Hls && window.Hls.isSupported()) {
          var hls = new window.Hls({
            capLevelToPlayerSize: true,
            // Start on the lowest rendition so the first frame arrives fast
            // instead of buffering a big segment (which read as a stutter).
            startLevel: 0,
            // Hero clips are short and loop forever. Buffer the whole thing and
            // never evict what has played, so each loop replays from memory
            // with no re-fetch — that re-fetch was the jerk at the loop point.
            maxBufferLength: 60,
            maxMaxBufferLength: 120,
            backBufferLength: Infinity,
          });
          hls.loadSource(hlsUrl);
          hls.attachMedia(video);
          hls.on(window.Hls.Events.MANIFEST_PARSED, function () { playSafe(video); });
          hls.on(window.Hls.Events.ERROR, function (_e, data) {
            if (data && data.fatal) useMp4(video);
          });
        } else {
          useMp4(video);
        }
      }).catch(function () { useMp4(video); });
      return;
    }

    useMp4(video);
  }

  function init() {
    var videos = document.querySelectorAll('video[data-hls]');
    for (var i = 0; i < videos.length; i++) setup(videos[i]);
  }

  // Pre-warm hls.js in parallel with the page so the first frame is not
  // gated on a serial library download (non-Safari only).
  function prewarm() {
    var v = document.querySelector('video[data-hls]');
    if (!v) return;
    var lib = v.dataset.hlsLib;
    if (lib && !v.canPlayType('application/vnd.apple.mpegurl')) {
      loadLib(lib).catch(function () {});
    }
  }
  prewarm();

  // The script is deferred, so the DOM is already parsed by the time this
  // runs. Waiting for window.load (every image finished) left the poster
  // frozen and made the video visibly pop in late.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
